import * as React from "react";
import { useState, useEffect } from "react";
import { default as axios } from "axios";
import Select, { components } from "react-select";
import Grid from "@mui/material/Grid";
//Components, Styles, State, Images
import DataTable from "../DataTable/DataTable";
import styles from "./MainPage.module.scss";
import { StateList, state } from "../config";
import Descriptions from "../Components/Descriptions/Descriptions";
import NavBar from "../Components/NavigationBar/NavBar";
import LoadingBar from "../Components/LoadingBar/LoadingBar";
import { api } from "../../utils/useAxios";

import { trace } from "@opentelemetry/api";
import { useAuth0 } from "@auth0/auth0-react";
import { trackTopicClick, trackStateClick } from "../../utils/datadog-metrics";

type TopicType = {
  value: string;
  label: string;
};

type HeaderType = {
  value: string;
  label: string;
  topic: string;
};

//Main Page component. This holds the table filter page and its children
const MainPage = (props: any): React.JSX.Element => {
  const { callTopics, callUserTopics, callDescriptions } = state;

  const [topicsList, setTopicsList] = useState(state.topicsList);
  const [headerList, setHeaderList] = useState(state.headerList);
  const [pickedTopics, setPickedTopics] = useState(state.pickedTopics);
  const [pickedHeaders, setPickedHeaders] = useState(state.pickedHeaders);
  const [pickedStates, setPickedStates] = useState(state.pickedStates);
  const [descriptionsList, setDescriptionsList] = useState(
    state.descriptionsList,
  );
  const [isLoading, setIsLoading] = useState(state.isLoading);
  const [data, setData] = useState(state.data);
  const [lastUpdated, setLastUpdated] = useState(state.lastUpdated);
  const [firstPickedState, setFirstPickedState] = useState({});
  const { user } = useAuth0();

  //When the component mounts, pull the correct topic and description lists from the API for admin or user
  useEffect(() => {
    let topicQuery = {};
    user["https://www.magmutual.com/Auth0_Role"].includes("Opus-admin")
      ? (topicQuery = callTopics)
      : (topicQuery = callUserTopics);
    api({
      method: "post",
      url: "getOpusTopics",
      data: topicQuery,
    }).then((response) => {
      let tempTopics = response.data.topics.map((topic) => {
        return topic;
      });
      tempTopics.sort(function (a, b) {
        return a.localeCompare(b);
      });
      let tempList = [];
      tempTopics.forEach((topic) => {
        let tempInsert = {
          value: topic,
          label: topic,
        };
        tempList.push(tempInsert);
      });
      setTopicsList(tempList);
    });

    api({
      method: "post",
      url: "getOpusDescriptions",
      data: callDescriptions,
    }).then((response) => {
      let tempDescriptions = JSON.stringify(response.data.Group);
      setDescriptionsList(JSON.parse(tempDescriptions));
    });
  }, []);

  //Checks if there is states selected in the state drop-down. If so, performs table update
  const handleTopics = (e: TopicType[]): void => {
    if (e.length === 0) {
      setPickedTopics([]);
      setPickedHeaders([]);
      setHeaderList([]);
      buildTable();
    }
    let topicsArray = e.filter(
      (o1) => !pickedTopics.some((o2) => o1.value === o2.value),
    );
    if (topicsArray.length !== 0) {
      // DataDog metric for topic click
      trackTopicClick(topicsArray[0].label || topicsArray[0].value);
    }
    setPickedTopics(e);
  };
  //When the pickedTopics state changes, get the headers for the selected topics
  useEffect(() => {
    getHeaders();
  }, [pickedTopics]);

  //Retrieves a list of Headers relating to the selected topics.
  const getHeaders = (): void => {
    let tempArray = [];
    let tempList = [];
    pickedTopics.map((topic) => {
      api({
        method: "post",
        url: "getTopicHeaders",
        data: {
          query: "SELECT header FROM opus WHERE topic='" + topic.value + "'",
        },
      })
        .then((response) => {
          tempArray.push({
            topic: topic.value,
            headers: response.data.headers,
          });
          response.data.headers.forEach((header) => {
            let tempString = header.replace("'", "''");
            let tempInsert = {
              value: tempString,
              label: header + "(" + topic.label + ")",
              topic: topic.value,
            };
            tempList.push(tempInsert);
          });
        })
        .then(() => {
          setHeaderList(tempList);
          setPickedHeaders(tempList);
          if (pickedStates.length !== 0) {
            buildTable();
          }
        });
    });
  };

  //Sets and clears the pickedHeaders State
  const handleHeaders = (e: HeaderType[]): void => {
    if (e.length === 0) {
      setPickedHeaders([]);
      buildTable();
    } else {
      setPickedHeaders(e);
      buildTable();
    }
  };

  //Sets and clears the pickedStates State
  const handleStates = (e: TopicType[]): void => {
    if (e.length === 0) {
      setPickedStates([]);
      buildTable();
    }
    let statesArray = e.filter(
      (o1) => !pickedStates.some((o2) => o1.value === o2.value),
    );
    if (statesArray.length !== 0) {
      // DataDog metric for state click
      trackStateClick(statesArray[0].label || statesArray[0].value);
    }
    setPickedStates(e);
    //used just for the first state selected
    setFirstPickedState(e);
  };
  //When the firstPickedStates state changes, build the table
  useEffect(() => {
    buildTable();
  }, [firstPickedState]);

  //Creates the state column and with key value pairs
  const buildColumns = (): void => {
    let stateColumns = [
      {
        id: "topic",
        label: "Topic",
        align: "center",
      },
      {
        id: "header",
        label: "Header",
        align: "center",
      },
    ];
    pickedStates.forEach((state) => {
      let insert = {
        id: state.value,
        label: state.label,
        align: "center",
      };
      stateColumns.push(insert);
    });
    setIsLoading(false);
  };

  //Asynchronously pulls data from the API where topic is equal to the selected topics.
  async function getData(option: TopicType): Promise<any> {
    const response = await api({
      method: "post",
      url: "getOpusData",
      data: { query: "SELECT * FROM opus WHERE topic='" + option.value + "'" },
    });
    return response;
  }

  //This recursively generates the table structure and sets the table data for the selected states
  const buildTable = (): void => {
    setIsLoading(true);
    let tempData = [];
    let tempStates = [];
    let tempObject = {};
    for (const option of pickedTopics) {
      getData(option).then((response) => {
        let data = response.data.Group;
        tempData.push(data);
        //if the topic is not present in the lastUpdated object then add the last updated value to the list
        if (!lastUpdated[option]) {
          // create temporary object to hold the lastUpdated values, so as not to directly modify the state,
          // then set the new state
          tempObject = lastUpdated;
          // pull the lastUpdated value from the object pulled from the backend,
          // each index is an column object holding the values for each (header, topic) key-pair
          tempObject[option] = data[0]["lastUpdated"];
        }
      });
    }
    pickedStates.forEach((state) => {
      tempStates.push(state);
    });
    setData(tempData);
    setPickedStates(tempStates);
    setLastUpdated(tempObject);
    setTimeout(buildColumns, 2000);
  };

  //Finds the column relating to selected headers
  const hasHeader = (headers: HeaderType[], header: HeaderType): boolean => {
    let isThere = false;
    for (const item of headers) {
      if (item.value === header.value && item.topic === header.topic) {
        isThere = true;
        break;
      }
    }
    return isThere;
  };

  //CSV build function for rotating the data to be exported as CSV
  const buildCSVTable = (props: {
    data: any[];
    pickedStates: TopicType[];
    pickedHeaders: HeaderType[];
  }): any[] => {
    let csvTable = [];
    if (typeof props !== "undefined") {
      props.data.map((data) => {
        data = data.filter((data) => {
          let tempString = data.header.replace("'", "''");
          let tempTopicHeader: HeaderType = {
            label: "",
            value: tempString,
            topic: data.topic,
          };
          return hasHeader(props.pickedHeaders, tempTopicHeader);
        });
        // Check if the data is empty due to a state change
        if (data.length === 0) {
          return null;
        }
        let csvHeader = [];
        csvHeader.push(data[0].topic);
        props.pickedHeaders.map((header) => {
          if (data[0].topic === header.topic) {
            csvHeader.push(header.value);
          }
        });
        csvTable.push(csvHeader);
        if (typeof props.pickedStates !== "undefined") {
          props.pickedStates.map((state) => {
            let csvStateRow = [];
            csvStateRow.push(state.label);
            data.map((topic) => {
              let stateTopic = state.value;
              csvStateRow.push(topic[stateTopic]);
            });
            csvTable.push(csvStateRow);
          });
        } else {
          setTimeout("250");
        }
      });
    }
    return csvTable;
  };

  const handleSelectAll = (e: React.BaseSyntheticEvent, props: any): void => {
    e.preventDefault();
    let current = props.selectProps;
    // If all options are selected clear them else set them all
    if (current.value.length === current.options.length) {
      props.clearValue();
    } else {
      props.setValue(props.options);
    }
  };

  const MenuList = (props: any): React.JSX.Element => {
    let current = props.selectProps;
    return (
      // TODO: Find a way to remove onClick from div to fix sonar-lint error
      <components.MenuList {...props}>
        {/* WHERE IS THIS STYLING?? */}
        <div
          className="form-select-all"
          onClick={(e) => handleSelectAll(e, props)}
        >
          <label>
            <input
              type="checkbox"
              defaultChecked={current.value.length === current.options.length}
            />
            {current.value.length === current.options.length
              ? "Deselect"
              : "Select"}{" "}
            all
          </label>
        </div>
        {props.children}
      </components.MenuList>
    );
  };

  return (
    <div className={styles.MainDiv}>
      <NavBar
        csvData={buildCSVTable({
          data: data,
          pickedHeaders: pickedHeaders,
          pickedStates: pickedStates,
        })}
        data={buildCSVTable({
          data: data,
          pickedHeaders: pickedHeaders,
          pickedStates: pickedStates,
        })}
        pickedStates={pickedStates}
      />
      <Grid container>
        <Grid item xs={12}>
          <div className={styles.selectMenuLabel} style={{ width: "3rem" }}>
            Topics
          </div>
          <Select
            styles={{
              menu: (provided) => ({ ...provided, zIndex: 9999 }),
            }}
            isDisabled={props.isDisabled}
            className={styles.selectMenu}
            closeMenuOnSelect={false}
            isSearchable
            placeholder="Topics"
            options={topicsList}
            onChange={handleTopics}
            isMulti
            value={pickedTopics}
          />
        </Grid>
        <Grid item xs={12}>
          <div className={styles.selectMenuLabel} style={{ width: "3rem" }}>
            States
          </div>
          <Select
            styles={{
              menu: (provided) => ({ ...provided, zIndex: 9999 }),
            }}
            isDisabled={props.isDisabled}
            className={styles.selectMenu}
            closeMenuOnSelect={false}
            isSearchable
            placeholder="States"
            options={StateList}
            onChange={handleStates}
            isMulti
            value={pickedStates}
            components={{ MenuList }}
          />
        </Grid>
        <Grid item xs={12} sx={{ mt: "0.7vh" }}>
          <div className={styles.selectMenuLabel} style={{ width: "4rem" }}>
            Headers
          </div>
          <Select
            styles={{
              menu: (provided) => ({ ...provided, zIndex: 9999 }),
            }}
            className={styles.selectMenu}
            isDisabled={props.isDisabled}
            closeMenuOnSelect={false}
            isSearchable
            placeholder="Headers"
            options={headerList}
            onChange={handleHeaders}
            isMulti
            value={pickedHeaders}
          />
        </Grid>
        <Grid item xs={12}>
          <div className={styles.mainDescriptionLabel}>Descriptions</div>
          <div className={styles.mainDescriptions}>
            <Descriptions
              descriptionsList={descriptionsList}
              pickedTopics={pickedTopics}
            />
          </div>
        </Grid>
        <Grid item xs={12}>
          {isLoading ? <LoadingBar color="inherit" /> : null}
          <DataTable
            data={data}
            pickedTopics={pickedTopics}
            pickedHeaders={pickedHeaders}
            pickedStates={pickedStates}
            style={{ zIndex: "-1" }}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default MainPage;
